import React from 'react';
import styles from '../styles/ContentCard.module.css';

/**
 * News Card Component
 * Displays news item cards
 *
 * Props:
 * - headline: News headline
 * - sourceUrl: URL to the news article
 * - publishedDate: Publication date (ISO string or formatted date)
 * - summary: Optional news summary
 * - heroImage: Optional hero image URL
 * - id: News item ID
 * - onClick: Optional click handler (receives id)
 */
function NewsCard({ headline, sourceUrl, publishedDate, summary, heroImage, id, onClick }) {
  // Format date to relative time
  const formatDate = (dateString) => {
    if (!dateString) return null;

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Extract domain from source URL
  const displaySource = sourceUrl ? new URL(sourceUrl).hostname.replace('www.', '') : '';

  return (
    <div
      className={styles.contentCard}
      onClick={() => onClick?.(id, { headline })}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {heroImage && (
        <div className={styles.contentCardImage}>
          <img src={heroImage} alt={headline} className={styles.image} />
          <div className={styles.contentCardBadge}>News</div>
        </div>
      )}

      <div className={styles.contentCardContent}>
        {!heroImage && <div className={styles.contentCardBadge}>News</div>}
        <div className={styles.contentCardTitle}>{headline}</div>

        {summary && (
          <div className={styles.contentCardDescription}>
            {summary}
          </div>
        )}

        <div className={styles.contentCardMeta}>
          {displaySource && (
            <div className={styles.contentCardMetaItem}>
              {displaySource}
            </div>
          )}
          {publishedDate && (
            <div className={styles.contentCardMetaItem}>
              {formatDate(publishedDate)}
            </div>
          )}
          <div className={styles.contentCardMetaItem}>
            Read More
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewsCard;
